/**
 * webpush.js — client helpers to register the service worker,
 * request permission, and subscribe/unsubscribe from web-push.
 *
 * We only touch these APIs when the browser actually supports them
 * (feature-detected inside every helper).  On unsupported browsers
 * we return `{ supported: false }` so the caller can render an
 * unobtrusive fallback.
 */
import axios from 'axios';

const SW_URL = '/sw.js';

// Convert a base64url public key (what the server emits) to a Uint8Array
// suitable for `applicationServerKey`.
const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const currentPermission = () =>
  (typeof window !== 'undefined' && 'Notification' in window)
    ? Notification.permission
    : 'default';

export const registerServiceWorker = async () => {
  if (!isPushSupported()) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    if (existing) return existing;
    return await navigator.serviceWorker.register(SW_URL);
  } catch (e) {
    return null;
  }
};

export const getExistingSubscription = async () => {
  if (!isPushSupported()) return null;
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

export const enableWebPush = async (API) => {
  if (!isPushSupported()) {
    return { ok: false, reason: 'unsupported' };
  }
  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: 'sw_register_failed' };

  // Grab the server public key
  let publicKey;
  try {
    const { data } = await axios.get(`${API}/push/vapid-public-key`);
    publicKey = data?.public_key;
    if (!publicKey) return { ok: false, reason: 'no_public_key' };
  } catch (_) {
    return { ok: false, reason: 'no_public_key' };
  }

  // Ask the user
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    return { ok: false, reason: 'permission_denied', permission: perm };
  }

  // Subscribe with the push service
  const existing = await reg.pushManager.getSubscription();
  const sub = existing || await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const json = sub.toJSON();
  try {
    await axios.post(`${API}/push/subscribe`, {
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'server_reject' };
  }
};

export const disableWebPush = async (API) => {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) {
    await axios.delete(`${API}/push/subscribe`).catch(() => {});
    return { ok: true };
  }
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  try {
    await axios.delete(`${API}/push/subscribe`, { params: { endpoint } });
  } catch (_) { /* ignore */ }
  return { ok: true };
};

export const sendTestPush = async (API) => {
  try {
    const { data } = await axios.post(`${API}/push/test`);
    return { ok: true, delivered: data?.delivered_to_devices || 0 };
  } catch (_) {
    return { ok: false };
  }
};
